// frontend/src/contexts/ViewOnlyContext.tsx
// View-only mode: globally disable POST/PUT/DELETE buttons.
// Defaults ON in production builds (REACT_APP_API_BASE includes tayzacore.com).
// User toggle persisted in localStorage so refresh keeps the setting.

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

const STORAGE_KEY = 'taisacore_view_only';

const isProduction = (): boolean =>
  (process.env.REACT_APP_API_BASE || '').includes('tayzacore.com');

interface ViewOnlyContextValue {
  isViewOnly: boolean;
  setViewOnly: (next: boolean) => void;
  isProduction: boolean;
}

const ViewOnlyContext = createContext<ViewOnlyContextValue | null>(null);

interface ViewOnlyProviderProps {
  children: ReactNode;
}

export const ViewOnlyProvider: React.FC<ViewOnlyProviderProps> = ({ children }) => {
  const [isViewOnly, setIsViewOnlyState] = useState<boolean>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
    // No explicit user choice yet — default ON in production, OFF elsewhere
    return isProduction();
  });

  // Persist any change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, isViewOnly ? 'true' : 'false');
  }, [isViewOnly]);

  const setViewOnly = useCallback((next: boolean) => {
    setIsViewOnlyState(next);
  }, []);

  return (
    <ViewOnlyContext.Provider value={{ isViewOnly, setViewOnly, isProduction: isProduction() }}>
      {children}
    </ViewOnlyContext.Provider>
  );
};

export const useViewOnly = (): ViewOnlyContextValue => {
  const ctx = useContext(ViewOnlyContext);
  if (!ctx) {
    // Safe default: assume not view-only if used outside provider (dev/test paths)
    return { isViewOnly: false, setViewOnly: () => {}, isProduction: false };
  }
  return ctx;
};

export { ViewOnlyContext };
